import { isAxiosError } from 'axios'
import { ref } from 'vue'

/** Shown while a protected route is waiting out a deploy blip. */
export const authReconnecting = ref(false)

/** Wait at most this long before giving up without sending the user to login. */
export const AUTH_RETRY_BUDGET_MS = 45_000

export function isAuthenticationFailure(error: unknown): boolean {
  return isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 419)
}

/** 5xx, a dropped connection, or a timeout. A 401 or 419 is not a blip. */
export function isDeployBlip(error: unknown): boolean {
  if (isAuthenticationFailure(error)) {
    return false
  }
  if (!isAxiosError(error) || !error.response) {
    return true
  }

  return error.response.status >= 500
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * Load the current user, retrying through a rolling deploy.
 * The deadline is wall-clock time, including time spent inside each request.
 * Returns unauthenticated only for a real 401 or 419.
 */
export async function loadAuthenticatedUser(
  getUser: () => Promise<unknown>,
  options: {
    sleep?: (ms: number) => Promise<void>
    budgetMs?: number
    now?: () => number
  } = {}
): Promise<'authenticated' | 'unauthenticated' | 'unavailable'> {
  const wait = options.sleep ?? sleep
  const budgetMs = options.budgetMs ?? AUTH_RETRY_BUDGET_MS
  const now = options.now ?? Date.now
  const startedAt = now()
  let delay = 1000

  while (true) {
    const remaining = budgetMs - (now() - startedAt)
    if (remaining <= 0) {
      authReconnecting.value = false
      return 'unavailable'
    }

    let ignoreLateResult = false
    const attempt = getUser().then(
      () => 'done' as const,
      (error: unknown) => {
        if (ignoreLateResult) {
          return 'late' as const
        }
        throw error
      }
    )

    try {
      const outcome = await Promise.race([
        attempt,
        wait(remaining).then(() => {
          ignoreLateResult = true
          return 'timeout' as const
        })
      ])
      if (outcome === 'timeout') {
        authReconnecting.value = false
        return 'unavailable'
      }
      authReconnecting.value = false
      return 'authenticated'
    } catch (error) {
      if (isAuthenticationFailure(error)) {
        authReconnecting.value = false
        return 'unauthenticated'
      }
      const left = budgetMs - (now() - startedAt)
      if (!isDeployBlip(error) || left <= 0) {
        authReconnecting.value = false
        return 'unavailable'
      }

      const step = Math.min(delay, left)
      authReconnecting.value = true
      await wait(step)
      delay = Math.min(delay * 2, 8000)
    }
  }
}
